// Crypto Wallets → Configuration → Asset Rates (SM-507).
//
// ONE page for every top-up rate on the platform. That is the whole
// design constraint and it is deliberate: a rate is a per-(asset,
// currency) fact, and the moment rates are managed per asset, or per
// currency, or behind a mapping's detail page, an operator can no
// longer answer "what is priced and what is not" without walking the
// catalogue.
//
// So the table is the CROSS PRODUCT of assets and creditable
// currencies, not a list of the rates that happen to exist. A mapping
// nobody has priced is a row that says so — a deposit against it is
// detected and credits nothing, which is exactly the state that is
// invisible when a table only lists what was configured.
//
// Everything an operator can do to a rate — set, replace, retire,
// review its history, dry-run a credit — happens in the expanded row.
// No rate has a page of its own.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";

import { Card, CardBody } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { TechHint } from "@/components/ui/TechHint";
import { TokenAmountInput } from "@/components/ui/TokenAmountInput";
import { CurrencyAmount, CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { useCurrencyPresentation } from "@/components/currency/CurrencyPresentationProvider";
import { campaignCurrency } from "@/lib/currency";
import { errMsg, td, th, thRight, useCanConfig } from "@/components/wallet-ops/configShared";
import {
  buildRateRows,
  effectiveMinorPerUnit,
  filterRateRows,
  needsUnitReview,
  previewCreditMinor,
  RATE_CURRENCIES,
  rateDecimals,
  rateMajorToMinor,
  rateMinorToMajor,
  rowKey,
  type RateRow,
} from "@/components/wallet-ops/rateShared";
import {
  fetchCryptoAssets,
  fetchCryptoNetworks,
  fetchCryptoRates,
  previewCryptoCredit,
  retireCryptoRate,
  setCryptoRate,
  walletKeys,
} from "@/lib/wallet-queries";
import type { CryptoPreviewCredit, CryptoRate } from "@/lib/wallet-types";
import { formatTokenAmount } from "@/lib/tokenFmt";
import { fmtDate, fmtRelative } from "@/lib/format";
import { useAdminMap, adminName } from "@/hooks/useAdminNames";

/**
 * The filter and deep-link state, all of it in the URL. A rate is the
 * thing operators send each other links to ("this one is wrong"), and a
 * link that lands on an unfiltered table with nothing open is not a
 * link to a rate.
 */
interface RatesSearch {
  q?: string;
  currency?: string;
  network?: string;
  missing?: boolean;
  /**
   * Only pairings whose live rate predates the SM-513 unit fix — the
   * operator review list. Its own filter rather than a sort, because
   * the answer an operator needs is "which prices has nobody vouched
   * for", and that is a set, not an ordering.
   */
  review?: boolean;
  /**
   * The expanded row, as "assetID|currency". Deliberately NOT the
   * `currency` filter: a link to one pairing should not also hide the
   * same asset's other pairing, which is usually the next thing the
   * operator wants to look at.
   */
  open?: string;
}

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);

export const Route = createFileRoute("/_app/wallet-ops/config/asset-rates")({
  validateSearch: (search: Record<string, unknown>): RatesSearch => {
    const currency = str(search.currency);
    return {
      q: str(search.q),
      currency: RATE_CURRENCIES.includes(currency as never) ? currency : undefined,
      network: str(search.network),
      missing: search.missing === true || search.missing === "true" ? true : undefined,
      review: search.review === true || search.review === "true" ? true : undefined,
      open: str(search.open),
    };
  },
  component: AssetRatesPage,
});

function AssetRatesPage() {
  const canConfig = useCanConfig();
  const navigate = useNavigate();
  const search = Route.useSearch();

  const networks = useQuery({
    queryKey: walletKeys.networksList(false),
    queryFn: () => fetchCryptoNetworks(),
  });
  const assets = useQuery({
    queryKey: walletKeys.assets(),
    queryFn: () => fetchCryptoAssets(),
  });
  const assetList = useMemo(() => assets.data ?? [], [assets.data]);

  // One query per asset, because that is the shape the service offers
  // (a rate list is asset-scoped). They are merged into one table here
  // rather than shown as one section per asset.
  const rateQueries = useQueries({
    queries: assetList.map((a) => ({
      queryKey: walletKeys.rates(a.asset_id),
      queryFn: () => fetchCryptoRates(a.asset_id),
    })),
  });

  const rows = useMemo(() => {
    const networkNames: Record<string, string> = {};
    for (const n of networks.data ?? []) networkNames[n.network_id] = n.name || n.network_id;
    const ratesByAsset: Record<
      string,
      { rates?: CryptoRate[]; loading?: boolean; error?: unknown }
    > = {};
    assetList.forEach((a, i) => {
      const q = rateQueries[i];
      ratesByAsset[a.asset_id] = {
        rates: q?.data,
        loading: q?.isLoading ?? false,
        error: q?.isError ? q.error : undefined,
      };
    });
    return buildRateRows({ assets: assetList, networkNames, ratesByAsset });
  }, [assetList, networks.data, rateQueries]);

  const visible = useMemo(
    () =>
      filterRateRows(rows, {
        q: search.q,
        currency: search.currency,
        network: search.network,
        missing: search.missing,
        review: search.review,
      }),
    [rows, search.q, search.currency, search.network, search.missing, search.review],
  );

  const setSearch = (next: Partial<RatesSearch>) =>
    navigate({ to: "/wallet-ops/config/asset-rates", search: (s) => ({ ...s, ...next }), replace: true });

  // The open row lives in the URL too, so "here is the rate I mean" is
  // a link rather than a set of instructions.
  const openKey = search.open ?? null;
  const toggleRow = (row: RateRow) => {
    const key = rowKey(row.asset.asset_id, row.currency);
    setSearch({ open: openKey === key ? undefined : key });
  };

  const missingCount = rows.filter((r) => !r.active && !r.loading).length;
  const reviewCount = rows.filter((r) => needsUnitReview(r.active)).length;
  const loading = assets.isLoading || networks.isLoading;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="font-display text-2xl tracking-tight">Asset rates</h1>
        <p className="max-w-[85ch] text-sm text-muted">
          Every asset–currency pairing and what a deposit of it credits.
          Rates are whole currency units per one whole token, so 100 means one
          token is worth one hundred, not one. Pairings with no rate are
          listed too: deposits against them are detected and credit nothing,
          which is worth seeing rather than inferring. The raw wire value
          shows beneath each field.
        </p>
        <p className="max-w-[85ch] text-xs text-t-3">
          Each rate belongs to one pairing. Repricing an asset in one currency
          never moves what it is worth in another, and never touches another
          asset on the same network.{" "}
          <Link
            to="/wallet-ops/config/rails"
            className="underline underline-offset-2 hover:text-t-1"
          >
            Whether a route is open is decided on Deposit &amp; Withdrawal
          </Link>
        </p>
      </header>

      <RateFilterBar
        search={search}
        networks={(networks.data ?? []).map((n) => ({
          value: n.network_id,
          label: n.name || n.network_id,
        }))}
        missingCount={missingCount}
        reviewCount={reviewCount}
        shown={visible.length}
        total={rows.length}
        onChange={setSearch}
      />

      <Card>
        <CardBody className="p-0">
          {loading && (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}
          {!loading && rows.length === 0 && (
            <p className="px-5 py-10 text-center text-sm text-muted">
              No assets are configured yet, so there is nothing to price.
            </p>
          )}
          {!loading && rows.length > 0 && visible.length === 0 && (
            <p className="px-5 py-10 text-center text-sm text-muted">
              No pairing matches these filters.
            </p>
          )}
          {visible.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Asset</th>
                    <th className={th}>Network</th>
                    <th className={th}>Currency</th>
                    <th className={th}>Current rate</th>
                    <th className={th}>Status</th>
                    <th className={th}>Effective</th>
                    <th className={th}>Set by</th>
                    <th className={thRight}>Last updated</th>
                    <th className={thRight} />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <RateTableRow
                      key={rowKey(row.asset.asset_id, row.currency)}
                      row={row}
                      open={openKey === rowKey(row.asset.asset_id, row.currency)}
                      canConfig={canConfig}
                      onToggle={() => toggleRow(row)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// --- filters ---

function RateFilterBar({
  search,
  networks,
  missingCount,
  reviewCount,
  shown,
  total,
  onChange,
}: {
  search: RatesSearch;
  networks: { value: string; label: string }[];
  missingCount: number;
  reviewCount: number;
  shown: number;
  total: number;
  onChange: (next: Partial<RatesSearch>) => void;
}) {
  return (
    <Card>
      <CardBody className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Input
            label="Search"
            value={search.q ?? ""}
            onChange={(e) => onChange({ q: e.target.value || undefined })}
            placeholder="symbol, asset id or network"
          />
        </div>
        <Select
          label="Currency"
          value={search.currency ?? ""}
          onChange={(e) => onChange({ currency: e.target.value || undefined })}
          placeholder="All currencies"
          options={RATE_CURRENCIES.map((c) => ({ value: c, label: c }))}
        />
        <Select
          label="Network"
          value={search.network ?? ""}
          onChange={(e) => onChange({ network: e.target.value || undefined })}
          placeholder="All networks"
          options={networks}
        />
        <label className="flex items-center gap-2 self-end pb-2.5 text-xs text-t-2">
          <input
            type="checkbox"
            checked={search.missing === true}
            onChange={(e) => onChange({ missing: e.target.checked || undefined })}
            aria-label="Only pairings with no rate"
          />
          Missing a rate
          {missingCount > 0 && <Badge tone="warning">{missingCount}</Badge>}
        </label>
        <label className="flex items-center gap-2 self-end pb-2.5 text-xs text-t-2">
          <input
            type="checkbox"
            checked={search.review === true}
            onChange={(e) => onChange({ review: e.target.checked || undefined })}
            aria-label="Only rates set before the unit fix"
          />
          Needs unit review
          {reviewCount > 0 && <Badge tone="warning">{reviewCount}</Badge>}
        </label>
        <p className="self-end pb-3 text-2xs text-t-3">
          {`${shown} of ${total} pairings`}
        </p>
      </CardBody>
    </Card>
  );
}

// --- one pairing ---

function RateTableRow({
  row,
  open,
  canConfig,
  onToggle,
}: {
  row: RateRow;
  open: boolean;
  canConfig: boolean;
  onToggle: () => void;
}) {
  const admins = useAdminMap();
  const { asset, active } = row;
  return (
    <Fragment>
      <tr
        className={`cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2/60 ${
          open ? "bg-surface-2/60" : ""
        }`}
        onClick={onToggle}
      >
        <td className={`${td} px-5 font-medium text-fg`}>
          {asset.symbol}
          <span className="ml-2 font-mono text-2xs text-muted">{asset.asset_id}</span>
        </td>
        <td className={td} title={asset.network_id}>
          {row.networkName}
        </td>
        <td className={td}>
          <CurrencyBadge currency={row.currency} />
        </td>
        <td className={`${td} num`}>
          {row.loading ? (
            <Skeleton className="h-4 w-28" />
          ) : row.error ? (
            <span className="text-2xs text-no">{errMsg(row.error)}</span>
          ) : active ? (
            <TechHint
              tech={`rate ${active.rate_num}/${active.rate_den} × ${active.spark_minor_per_unit} minor/unit`}
            >
              1 {asset.symbol} ={" "}
              <CurrencyAmount
                value={effectiveMinorPerUnit(active)}
                currency={row.currency}
                className="font-semibold"
              />
            </TechHint>
          ) : (
            <span className="text-2xs text-t-3">not priced</span>
          )}
        </td>
        <td className={td}>
          {row.loading ? null : active ? (
            needsUnitReview(active) ? (
              // Still crediting, still the live rate — the badge says
              // the PROVENANCE is unknown, not that the rail is down.
              <Badge tone="warning" title="Set before the unit fix — confirm the figure">
                Review units
              </Badge>
            ) : (
              <Badge tone="success">Active</Badge>
            )
          ) : (
            <Badge tone="warning">No rate</Badge>
          )}
        </td>
        <td className={`${td} text-2xs text-muted`}>
          {active ? fmtDate(active.created_at) : "—"}
        </td>
        <td className={td} title={active?.created_by ?? undefined}>
          {active ? adminName(admins, active.created_by) : "—"}
        </td>
        <td className={`${td} text-right text-2xs text-muted`}>
          {active ? fmtRelative(active.created_at) : "—"}
        </td>
        <td className={`${td} text-right`}>
          <button
            type="button"
            aria-expanded={open}
            aria-label={`Manage the ${asset.symbol} ${row.currency} rate`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="rounded px-2 py-1 text-muted hover:bg-surface-2 hover:text-fg"
          >
            <Chevron open={open} />
          </button>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-border/60 last:border-0">
          <td colSpan={9} className="bg-surface-2/30 px-5 py-4">
            <RatePanel row={row} canConfig={canConfig} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={`transition-transform ${open ? "rotate-180" : ""}`}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/**
 * What a deposit would credit at the rate CURRENTLY TYPED — computed
 * here, not fetched (SM-513 AC4).
 *
 * The server dry run below prices against the rate that is SAVED, which
 * is the one figure an operator setting a new rate does not want. This
 * one answers the question they actually have: if I save this, what
 * does a deposit earn? It runs the same integer floor the service runs,
 * so the two agree on any amount they are both asked about.
 *
 * The rows are fixed sizes rather than an input because the point is
 * the SHAPE of the rate — whole, fractional and dust, side by side, is
 * what makes a factor-of-100 error obvious at a glance.
 */
function CreditPreview({
  symbol,
  currency,
  decimals,
  minorPerUnit,
}: {
  symbol: string;
  currency: string;
  decimals: number;
  minorPerUnit: number;
}) {
  const samples = useMemo(() => {
    const whole = 10n ** BigInt(Math.max(0, decimals));
    // The ticker is joined at render, not here. A template literal
    // that starts with copy — `0.5 ${symbol}` — lands in the
    // translation catalogue as a phrase, and "0.5 USDT" is a number,
    // not something to translate.
    return [
      { scale: "1", base: whole },
      { scale: "0.5", base: whole / 2n },
      { scale: "0.01", base: whole / 100n },
    ]
      .filter((s) => s.base > 0n)
      .map((s) => ({
        ...s,
        result: previewCreditMinor(s.base.toString(), decimals, minorPerUnit),
      }));
  }, [symbol, decimals, minorPerUnit]);

  return (
    <div className="rounded-md border border-border/60 bg-surface-2/40 p-3">
      <p className="mb-2 text-2xs font-semibold uppercase tracking-wide text-t-3">
        What a deposit would credit
      </p>
      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        {samples.map((s) => (
          <div key={s.scale} className="flex items-baseline gap-2">
            <dt className="text-2xs text-muted">
              {s.scale} {symbol}
            </dt>
            <dd className="num">
              {s.result ? (
                <TechHint
                  tech={`${s.base} base units → ${s.result.credited} minor${
                    s.result.remainder > 0n ? ` (remainder ${s.result.remainder})` : ""
                  }`}
                >
                  <CurrencyAmount
                    value={Number(s.result.credited)}
                    currency={currency}
                  />
                </TechHint>
              ) : (
                "—"
              )}
            </dd>
          </div>
        ))}
      </dl>
      {samples.some((s) => s.result && s.result.credited === 0n) && (
        <p className="mt-2 text-2xs text-warning">
          {`At this rate some deposits are worth less than one ${currency} minor unit and would credit nothing.`}
        </p>
      )}
    </div>
  );
}

// The whole life of ONE pairing's rate, inline. Mounted only for the
// open row, so form and preview state reset when the operator moves on.
function RatePanel({ row, canConfig }: { row: RateRow; canConfig: boolean }) {
  const qc = useQueryClient();
  const admins = useAdminMap();
  const { asset, currency, active, history } = row;
  const [tab, setTab] = useState<"current" | "history">("current");

  // The currency's OWN wire scale — the same number <CurrencyAmount>
  // divides by to draw every figure on this page. SM-513 was the two
  // disagreeing: the editor scaled the entry by 100 while the row it
  // wrote was rendered at 10 000, so "100" saved and read back "1.00".
  // Taking both from one place is what stops that being possible again.
  const { unitMinor } = useCurrencyPresentation(campaignCurrency(currency));

  // Operators think in whole currency units, the wire speaks minor ones.
  // The field opens on the LIVE rate rather than a constant: a form
  // that always says 100 invites an operator to re-save a figure they
  // never chose.
  //
  // Seeded on the first render that HAS a rate, not only on mount. A
  // deep link (?open=…) mounts this panel while the rate query is still
  // in flight, and a mount-time initialiser would leave that operator —
  // the one who followed a link to this exact rate — with an empty box.
  const [perUnit, setPerUnit] = useState("");
  const [seededFrom, setSeededFrom] = useState<string | null>(null);
  if (active && seededFrom !== active.rate_id && perUnit === "") {
    setSeededFrom(active.rate_id);
    setPerUnit(rateMinorToMajor(effectiveMinorPerUnit(active), unitMinor));
  }
  const enteredMinor = rateMajorToMinor(perUnit, unitMinor);
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: walletKeys.rates(asset.asset_id) });

  const save = useMutation({
    mutationFn: () => {
      if (enteredMinor === null || enteredMinor <= 0) {
        // Refuse rather than send 0: a zero rate is a rate that credits
        // nothing, and the service would take it.
        throw new Error(
          `Enter a positive rate with at most ${rateDecimals(unitMinor)} decimal places.`,
        );
      }
      return setCryptoRate(asset.asset_id, enteredMinor, currency);
    },
    onSuccess: invalidate,
    meta: { successMessage: "Rate set." },
  });
  const retire = useMutation({
    mutationFn: () => retireCryptoRate(asset.asset_id, currency),
    onSuccess: invalidate,
    meta: { successMessage: "Rate retired." },
  });

  const [previewAmount, setPreviewAmount] = useState("");
  const [preview, setPreview] = useState<CryptoPreviewCredit | null>(null);
  // Price the typed amount against the rate being EDITED where there is
  // one, and against the live rate otherwise. Falling back to the live
  // rate matters for the operator who only came to check what the
  // current price does.
  const previewRateMinor =
    enteredMinor && enteredMinor > 0
      ? enteredMinor
      : active
        ? effectiveMinorPerUnit(active)
        : null;
  const localPreviewIsEntered = !!enteredMinor && enteredMinor > 0;
  const localPreview =
    previewRateMinor !== null && previewAmount.trim() !== ""
      ? previewCreditMinor(previewAmount.trim(), asset.token_decimals, previewRateMinor)
      : null;
  const runPreview = useMutation({
    mutationFn: () => previewCryptoCredit(asset.asset_id, previewAmount.trim(), currency),
    onSuccess: (r) => setPreview(r),
  });

  const historyLabel = history.length > 0 ? `History (${history.length})` : "History";

  return (
    <div className="space-y-4">
      <SegmentedControl
        value={tab}
        onChange={setTab}
        options={[
          { value: "current", label: "Current" },
          { value: "history", label: historyLabel },
        ]}
      />

      {tab === "history" && (
        <>
          {row.loading && <Skeleton className="h-10" />}
          {!row.loading && history.length === 0 && (
            <p className="py-6 text-center text-sm text-muted">
              No superseded rates yet — the previous rate lands here whenever a
              new one is set or the live one is retired.
            </p>
          )}
          {history.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-4`}>Rate</th>
                    <th className={thRight}>Per unit</th>
                    <th className={th}>Status</th>
                    <th className={th}>Set by</th>
                    <th className={thRight}>When</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((r) => (
                    <tr key={r.rate_id} className="border-b border-border/60 last:border-0">
                      <td className={`${td} num px-4`}>
                        <TechHint
                          tech={`rate ${r.rate_num}/${r.rate_den} × ${r.spark_minor_per_unit} minor/unit`}
                        >
                          1 {asset.symbol} ={" "}
                          <CurrencyAmount
                            value={effectiveMinorPerUnit(r)}
                            currency={currency}
                          />
                        </TechHint>
                      </td>
                      <td className={`${td} num text-right`}>
                        {/* The same effective figure the Rate column
                            shows. Printing spark_minor_per_unit raw
                            dropped rate_num/rate_den, so a legacy 3/2
                            row read as two different prices in two
                            columns of one table (SM-513 AC3). */}
                        <TechHint tech={`${effectiveMinorPerUnit(r)} minor`}>
                          <CurrencyAmount
                            value={effectiveMinorPerUnit(r)}
                            currency={currency}
                          />
                        </TechHint>
                      </td>
                      <td className={td}>
                        <Badge tone="muted">Superseded</Badge>
                      </td>
                      <td className={td} title={r.created_by ?? undefined}>
                        {adminName(admins, r.created_by)}
                      </td>
                      <td className={`${td} text-right text-2xs text-muted`}>
                        {fmtRelative(r.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "current" && (
        <>
          {!row.loading && !active && (
            <div className="rounded-md border border-dashed border-border p-4">
              <p className="text-sm text-muted">
                {`Deposits of ${asset.symbol} cannot be credited in ${currency} until a rate is set${canConfig ? " below" : ""}.`}
              </p>
            </div>
          )}

          {/* The review notice states the rate as it stands and stops
              there. The platform will not multiply an ambiguous price by
              100 on an operator's behalf: only they know whether the
              figure below is the one they chose, and a wrong guess here
              would reprice a live rail by two orders of magnitude. */}
          {active && needsUnitReview(active) && (
            <div className="space-y-2 rounded-md border border-warning/40 bg-warning/5 p-4">
              <p className="text-sm">
                This rate was set before the unit fix. It currently credits{" "}
                <CurrencyAmount
                  value={effectiveMinorPerUnit(active)}
                  currency={currency}
                  className="font-semibold"
                />{" "}
                {`per whole ${asset.symbol}, and every deposit so far was credited at exactly that.`}
              </p>
              <p className="text-2xs text-muted">
                {canConfig
                  ? `The editor that wrote it scaled entries by 100 rather than by ${currency}'s ${unitMinor.toLocaleString()} minor units, so the operator may have typed a hundred times this figure. Confirm the rate by setting it again below — nothing changes until you do. To pause crediting for this pairing while you check, retire the rate.`
                  : "Confirm it with someone who can edit rates. Nothing changes until a new rate is set."}
              </p>
            </div>
          )}

          {canConfig && (
            <form
              className="space-y-3 rounded-md border border-border p-4"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate();
              }}
            >
              <p className="text-2xs font-semibold uppercase tracking-wide text-t-3">
                Set a new rate
              </p>
              <div className="max-w-sm">
                <Input
                  label={`${currency} units per 1 ${asset.symbol}`}
                  value={perUnit}
                  onChange={(e) => setPerUnit(e.target.value)}
                  inputMode="decimal"
                  hint={
                    enteredMinor !== null
                      ? `${enteredMinor} minor units on the wire`
                      : `Whole ${currency} units credited per one whole ${asset.symbol}, to at most ${rateDecimals(unitMinor)} decimal places.`
                  }
                />
              </div>
              {/* The equation, in the operator's own units and the page's
                  own formatter. It is the acceptance criterion made
                  visible: whatever is typed, this line says what the
                  platform will take it to mean, before it is saved. */}
              <p className="text-sm">
                {enteredMinor !== null && enteredMinor > 0 ? (
                  <>
                    1 {asset.symbol} ={" "}
                    <CurrencyAmount
                      value={enteredMinor}
                      currency={currency}
                      className="font-semibold"
                    />
                  </>
                ) : (
                  <span className="text-2xs text-t-3">
                    {`Enter a rate to see what one ${asset.symbol} would be worth.`}
                  </span>
                )}
              </p>
              {enteredMinor !== null && enteredMinor > 0 && (
                <CreditPreview
                  symbol={asset.symbol}
                  currency={currency}
                  decimals={asset.token_decimals}
                  minorPerUnit={enteredMinor}
                />
              )}
              <p className="text-2xs text-muted">
                {`Saving supersedes this pairing's active rate only — what ${asset.symbol} is worth in any other currency is untouched.`}
              </p>
              {save.isError && <p className="text-2xs text-no">{errMsg(save.error)}</p>}
              <div className="flex flex-wrap items-center gap-2">
                <Button type="submit" loading={save.isPending}>
                  Set rate
                </Button>
                {active && (
                  <Button
                    type="button"
                    variant="ghost"
                    loading={retire.isPending}
                    onClick={() => retire.mutate()}
                  >
                    Retire rate
                  </Button>
                )}
              </div>
              {retire.isError && <p className="text-2xs text-no">{errMsg(retire.error)}</p>}
              {active && (
                <p className="text-2xs text-t-3">
                  Retiring leaves the pairing configured but unpriced, and keeps
                  the retired rate in History.
                </p>
              )}
            </form>
          )}

          <div className="space-y-3 rounded-md border border-border p-4">
            <p className="text-2xs font-semibold uppercase tracking-wide text-t-3">
              Preview credit
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[240px] flex-1">
                <TokenAmountInput
                  label="Amount"
                  symbol={asset.symbol}
                  base={previewAmount}
                  decimals={asset.token_decimals}
                  onBase={setPreviewAmount}
                  allowEmpty
                />
              </div>
              <Button
                variant="secondary"
                loading={runPreview.isPending}
                disabled={previewAmount.trim() === ""}
                onClick={() => runPreview.mutate()}
              >
                Preview
              </Button>
            </div>
            {/* The answer arrives with the typing, not with a click
                (SM-513 AC4). It prices against the rate in the editor
                when one is entered and the live rate otherwise, so an
                operator can see what a rate does to a real amount
                BEFORE saving it — which is the only moment the check is
                worth anything. */}
            {localPreview && (
              <p className="text-sm">
                <TechHint tech={`${previewAmount.trim()} base units`}>
                  {formatTokenAmount(
                    previewAmount.trim(),
                    asset.token_decimals,
                    asset.symbol,
                  )}
                </TechHint>{" "}
                would credit{" "}
                <TechHint
                  tech={`${localPreview.credited} minor${
                    localPreview.remainder > 0n
                      ? ` (remainder ${localPreview.remainder})`
                      : ""
                  }`}
                >
                  <CurrencyAmount
                    value={Number(localPreview.credited)}
                    currency={currency}
                    className="font-semibold"
                  />
                </TechHint>{". "}
                <span className="text-2xs text-t-3">
                  {localPreviewIsEntered
                    ? "Priced at the rate entered above."
                    : "Priced at the live rate."}
                </span>
              </p>
            )}
            {runPreview.isError && (
              <p className="text-2xs text-no">{errMsg(runPreview.error)}</p>
            )}
            {preview && (
              <p className="text-sm">
                <TechHint tech={`${preview.amount_base} base units`}>
                  {formatTokenAmount(
                    preview.amount_base,
                    preview.token_decimals,
                    asset.symbol,
                  )}
                </TechHint>{" "}
                would credit{" "}
                <TechHint
                  tech={`${preview.credited_spark_minor} minor at ${preview.rate_num}/${preview.rate_den} × ${preview.spark_minor_per_unit}`}
                >
                  <CurrencyAmount
                    value={preview.credited_spark_minor}
                    currency={preview.credited_currency || currency}
                    className="font-semibold"
                  />
                </TechHint>
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
